import type { FieldPath } from 'react-hook-form';

import { ValueType } from 'components/DebounceSelect';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  AdditionalReference,
  Commodity,
  CompanyCoreInfo,
  Dropoff,
  Load,
  LoadStopsItem,
  NormalizedLoad,
  Pickup,
  TMSLocation,
  createInitCompanyCoreInfo,
  createInitLoad,
  createInitSpecs,
  createNewCommodity,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef, ObjectData, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import type { ManualFieldError } from 'utils/formValidation';
import { normalizeToArray } from 'utils/loadInfoAndBuilding';
import { parseToUtcWallClockDate } from 'utils/parseToUtcWallClockDate';

import { PLS_APPOINTMENT_TYPE } from './constants';
import {
  ensurePlsRouteStops,
  isPlsDropoffStopType,
  isPlsMultiStopRoute,
  normalizePlsRouteStopTypes,
  resolvePlsAppointmentType,
  syncPlsRouteStopsToPickupConsignee,
} from './plsRouteStops';
import {
  PlsAccessorial,
  PlsFormAccessorialItem,
  PlsFormValues,
  PlsLoadSize,
  PlsPartitionedReferenceFields,
  PlsReferenceFieldValues,
} from './types';

const PLS_CUSTOMER_LOCATION_QUALIFIER = 'plsCustomerLocationId';
const PLS_LOAD_SERVICE_TYPE_ID_QUALIFIER = 'plsLoadServiceTypeId';
const PLS_MISSING_OPERATIONAL_QUALIFIER = 'plsMissingOperational';
const PLS_BILL_TO_ADDRESS_NOTE_PREFIX = 'plsCustomerBillToAddressId=';
const PLS_BILL_TO_ADDRESS_NAME_NOTE_PREFIX = 'plsCustomerBillToAddressName=';
const PLS_DUPLICATE_SHIPMENT_NUMBER_ERROR_CODE = 'DUPLICATE_SHIPMENT_NUMBER';
const PLS_PURCHASE_ORDER_FIELD_CODE = 'PURCHASE_ORDER';

// Internal Drumkit qualifiers never become typed PLS reference inputs.
const PLS_INTERNAL_REFERENCE_QUALIFIERS = [
  PLS_CUSTOMER_LOCATION_QUALIFIER,
  PLS_LOAD_SERVICE_TYPE_ID_QUALIFIER,
  PLS_MISSING_OPERATIONAL_QUALIFIER,
] as const;

const PLS_STOP_WINDOW_DATETIME_FIELDS = [
  'readyTime',
  'readyTimeEnd',
  'mustDeliver',
  'mustDeliverEnd',
] as const;

function isPlsByAppointment(apptType: unknown): boolean {
  return String(apptType ?? '').trim() === PLS_APPOINTMENT_TYPE.ByAppointment;
}

/** Collapses By-appointment stops to a point-in-time window (earliest == latest). */
export function applyPlsAppointmentTypeToStopWindow<T extends ObjectData>(
  stop: T,
  kind: 'pickup' | 'dropoff'
): T {
  if (!stop || !isPlsByAppointment(stop.apptType)) {
    return stop;
  }

  if (kind === 'pickup') {
    return {
      ...stop,
      readyTimeEnd: stop.readyTime ?? '',
    };
  }

  return {
    ...stop,
    mustDeliverEnd: stop.mustDeliver ?? '',
  };
}

/** PLS form/create only use ready/mustDeliver windows; appt fields are unused Kit leftovers. */
function clearPlsAppointmentTimes<T extends ObjectData>(stop: T): T {
  return {
    ...stop,
    apptStartTime: '',
    apptEndTime: '',
  };
}

function coalescePlsStopTime(primary: unknown, fallback: unknown): unknown {
  if (primary != null && primary !== '') {
    return primary;
  }
  if (fallback != null && fallback !== '') {
    return fallback;
  }
  return primary ?? '';
}

function samePlsWallClockDate(first: unknown, second: unknown): boolean {
  const firstDate = parseToUtcWallClockDate(first);
  const secondDate = parseToUtcWallClockDate(second);

  return Boolean(
    firstDate &&
    secondDate &&
    firstDate.getUTCFullYear() === secondDate.getUTCFullYear() &&
    firstDate.getUTCMonth() === secondDate.getUTCMonth() &&
    firstDate.getUTCDate() === secondDate.getUTCDate()
  );
}

function preferMatchingPlsAppointmentTime(
  primary: unknown,
  appointment: unknown
): unknown {
  if (primary == null || primary === '') {
    return appointment ?? '';
  }

  return appointment != null &&
    appointment !== '' &&
    samePlsWallClockDate(primary, appointment)
    ? appointment
    : primary;
}

function resolvePlsStopEndTime(
  preferAppointmentTimes: boolean,
  primaryWasEmpty: boolean,
  primaryEnd: unknown,
  appointmentEnd: unknown
): unknown {
  if (preferAppointmentTimes) {
    return preferMatchingPlsAppointmentTime(primaryEnd, appointmentEnd);
  }
  if (primaryWasEmpty) {
    return coalescePlsStopTime(primaryEnd, appointmentEnd);
  }

  return primaryEnd ?? '';
}

/**
 * PLS origin/destination windows live on ready/mustDeliver fields only.
 * Promote Kit appt fields into those when the form earliest is empty, then drop appt.
 * Suggestion hydration may prefer a same-day explicit appointment over a generic date.
 * If earliest is already set, do not copy stale appt end into the *End field.
 * Empty appointment type is By appointment when earliest and latest match,
 * and FCFS otherwise. PLS does not store the type itself.
 */
export function normalizePlsStopAppointmentFields<T extends ObjectData>(
  stop: T | undefined,
  kind: 'pickup' | 'dropoff',
  preferAppointmentTimes = false
): T | undefined {
  if (!stop) {
    return stop;
  }

  if (kind === 'pickup') {
    const readyWasEmpty = stop.readyTime == null || stop.readyTime === '';
    const readyTime = preferAppointmentTimes
      ? preferMatchingPlsAppointmentTime(stop.readyTime, stop.apptStartTime)
      : coalescePlsStopTime(stop.readyTime, stop.apptStartTime);
    const readyTimeEnd = resolvePlsStopEndTime(
      preferAppointmentTimes,
      readyWasEmpty,
      stop.readyTimeEnd,
      stop.apptEndTime
    );
    const next = clearPlsAppointmentTimes({
      ...stop,
      readyTime,
      readyTimeEnd,
    });

    return {
      ...next,
      apptType: resolvePlsAppointmentType(
        stop.apptType,
        next.readyTime,
        next.readyTimeEnd
      ),
    };
  }

  const mustDeliverWasEmpty =
    stop.mustDeliver == null || stop.mustDeliver === '';
  const mustDeliver = preferAppointmentTimes
    ? preferMatchingPlsAppointmentTime(stop.mustDeliver, stop.apptStartTime)
    : coalescePlsStopTime(stop.mustDeliver, stop.apptStartTime);
  const mustDeliverEnd = resolvePlsStopEndTime(
    preferAppointmentTimes,
    mustDeliverWasEmpty,
    stop.mustDeliverEnd,
    stop.apptEndTime
  );
  const next = clearPlsAppointmentTimes({
    ...stop,
    mustDeliver,
    mustDeliverEnd,
  });

  return {
    ...next,
    apptType: resolvePlsAppointmentType(
      stop.apptType,
      next.mustDeliver,
      next.mustDeliverEnd
    ),
  };
}

/**
 * PLS consumes local wall-clock strings. Suggestion offsets identify the stop's
 * zone and must not be converted through the user's browser timezone.
 */
export function parsePlsSuggestionWallClock(value: unknown): Maybe<Date> {
  return parseToUtcWallClockDate(value);
}

export function formatPlsDateTime(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }

  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  // After denormalizeDatesForTMSForm (TZ-agnostic), UTC components hold the wall-clock
  // values — same approach as Aljex/MercuryGate toISOString paths.
  const pad = (part: number) => String(part).padStart(2, '0');

  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

export function formatPlsStopDatesForSubmit<T extends ObjectData>(
  stop: T,
  kind: 'pickup' | 'dropoff'
): T {
  if (!stop) {
    return stop;
  }

  const normalized = applyPlsAppointmentTypeToStopWindow(
    normalizePlsStopAppointmentFields(stop, kind) as ObjectData,
    kind
  );
  const formatted: ObjectData = { ...normalized };

  for (const field of PLS_STOP_WINDOW_DATETIME_FIELDS) {
    if (field in formatted && formatted[field]) {
      formatted[field] = formatPlsDateTime(formatted[field]);
    }
  }

  return clearPlsAppointmentTimes(formatted) as T;
}

export function formatPlsRouteStopDatesForSubmit(
  stop: LoadStopsItem
): LoadStopsItem {
  const kind = isPlsDropoffStopType(stop.stopType) ? 'dropoff' : 'pickup';

  const asFlat = {
    ...stop,
    readyTime: stop.readyTime,
    readyTimeEnd: stop.readyEndTime,
    mustDeliver: stop.mustDeliver,
    // Route stops store dropoff latest on readyEndTime (LoadStopsItem has no mustDeliverEnd).
    mustDeliverEnd: kind === 'dropoff' ? stop.readyEndTime : '',
    apptType: stop.apptType,
    apptStartTime: stop.apptStartTime,
    apptEndTime: stop.apptEndTime,
  };

  const formatted = formatPlsStopDatesForSubmit(asFlat, kind);

  // Prefer non-empty values: By-appointment dropoffs write the point-in-time onto
  // mustDeliverEnd while the form clears readyEndTime to ''.
  const latestWindow =
    kind === 'dropoff'
      ? coalescePlsStopTime(
          formatted.mustDeliverEnd,
          coalescePlsStopTime(formatted.readyTimeEnd, formatted.readyEndTime)
        )
      : coalescePlsStopTime(formatted.readyTimeEnd, formatted.readyEndTime);

  return clearPlsAppointmentTimes({
    ...stop,
    // Pickup windows use readyTime; dropoffs use mustDeliver. Clear the
    // inactive earliest field so a prior stop-type switch cannot leak it.
    readyTime: kind === 'dropoff' ? '' : String(formatted.readyTime ?? ''),
    readyEndTime: String(latestWindow ?? ''),
    mustDeliver: kind === 'dropoff' ? String(formatted.mustDeliver ?? '') : '',
    apptStartTime: '',
    apptEndTime: '',
  }) as LoadStopsItem;
}

/**
 * Suggestion hydration only. Promote a same-day appointment onto the route
 * window the form displays, then drop the unused appt fields. Submit must not
 * repeat this: leftover appt ends are hidden and must not replace the window.
 */
export function preferPlsRouteStopAppointmentWindow(
  stop: LoadStopsItem
): LoadStopsItem {
  const kind = isPlsDropoffStopType(stop.stopType) ? 'dropoff' : 'pickup';
  const normalized = normalizePlsStopAppointmentFields(
    {
      readyTime: stop.readyTime,
      readyTimeEnd: stop.readyEndTime,
      mustDeliver: stop.mustDeliver,
      mustDeliverEnd: kind === 'dropoff' ? stop.readyEndTime : '',
      apptType: stop.apptType,
      apptStartTime: stop.apptStartTime,
      apptEndTime: stop.apptEndTime,
    },
    kind,
    true
  );

  if (!normalized) {
    return stop;
  }

  const latestWindow =
    kind === 'dropoff' ? normalized.mustDeliverEnd : normalized.readyTimeEnd;

  return {
    ...stop,
    readyTime:
      kind === 'dropoff'
        ? stop.readyTime
        : ((normalized.readyTime ?? '') as LoadStopsItem['readyTime']),
    readyEndTime: (latestWindow ?? '') as LoadStopsItem['readyEndTime'],
    mustDeliver:
      kind === 'dropoff'
        ? ((normalized.mustDeliver ?? '') as LoadStopsItem['mustDeliver'])
        : stop.mustDeliver,
    apptType: String(normalized.apptType ?? stop.apptType ?? ''),
    apptStartTime: '',
    apptEndTime: '',
  };
}

export function toSelectStringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
}

export function plsAccessorialCodeFromValue(value: unknown): Undef<string> {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || !Number.isNaN(Number(trimmed))) {
    return undefined;
  }
  return trimmed;
}

type PlsCreateLoadFieldErrorResponse = {
  field?: unknown;
  path?: unknown;
  message?: unknown;
};

type PlsCreateLoadErrorDetailResponse = {
  field?: unknown;
  message?: unknown;
};

type PlsCreateLoadErrorResponse = {
  errorCode?: unknown;
  message?: unknown;
  details?: unknown;
  fieldErrors?: unknown;
};

export type GetPlsCreateLoadFieldErrorsOptions = {
  /** Used to point destination window errors at the last Route stop. */
  routeStopCount?: number;
  /**
   * Current route stops. When present, remaps stopoff earliest errors from
   * `readyTime` onto `mustDeliver` for intermediate DROP stops.
   */
  stops?: Array<{ stopType?: string } | undefined>;
};

export function getPlsCreateLoadFieldErrors(
  response: unknown,
  options?: GetPlsCreateLoadFieldErrorsOptions
): ManualFieldError<PlsFormValues>[] {
  const data = response as PlsCreateLoadErrorResponse | undefined;
  if (!data || typeof data !== 'object') {
    return [];
  }

  const routeStopCount = options?.routeStopCount;
  const stops = options?.stops;
  const fieldErrors = parsePlsCreateLoadFieldErrors(
    data.fieldErrors,
    routeStopCount,
    stops
  );
  if (fieldErrors.length > 0) {
    return fieldErrors;
  }

  const details = parsePlsCreateLoadDetails(data.details);
  const detailErrors = details
    .map((detail) => ({
      path: remapPlsRouteCreateLoadErrorPath(
        getPlsCreateLoadFormPath(detail.field, routeStopCount),
        routeStopCount,
        stops
      ) as FieldPath<PlsFormValues>,
      message: detail.message,
    }))
    .filter(
      (error): error is ManualFieldError<PlsFormValues> =>
        Boolean(error.path) && Boolean(error.message)
    );
  if (detailErrors.length > 0) {
    return detailErrors;
  }

  if (
    typeof data.errorCode === 'string' &&
    data.errorCode === PLS_DUPLICATE_SHIPMENT_NUMBER_ERROR_CODE
  ) {
    return [
      {
        path: 'plsOperationalFields.SHIPMENT_NUMBER' as FieldPath<PlsFormValues>,
        message:
          typeof data.message === 'string'
            ? data.message
            : 'Shipment number already exists in PLS.',
      },
    ];
  }

  return [];
}

function parsePlsCreateLoadFieldErrors(
  fieldErrors: unknown,
  routeStopCount?: number,
  stops?: Array<{ stopType?: string } | undefined>
): ManualFieldError<PlsFormValues>[] {
  if (!Array.isArray(fieldErrors)) {
    return [];
  }

  return fieldErrors
    .map((fieldError: PlsCreateLoadFieldErrorResponse) => {
      const rawPath =
        typeof fieldError.path === 'string'
          ? fieldError.path
          : getPlsCreateLoadFormPath(fieldError.field, routeStopCount);
      return {
        path: remapPlsRouteCreateLoadErrorPath(
          rawPath,
          routeStopCount,
          stops
        ) as FieldPath<PlsFormValues>,
        message:
          typeof fieldError.message === 'string' ? fieldError.message : '',
      };
    })
    .filter(
      (error): error is ManualFieldError<PlsFormValues> =>
        Boolean(error.path) && Boolean(error.message)
    );
}

function parsePlsCreateLoadDetails(
  details: unknown
): { field: string; message: string }[] {
  if (!Array.isArray(details)) {
    return [];
  }

  return details
    .map((detail: PlsCreateLoadErrorDetailResponse) => ({
      field: typeof detail.field === 'string' ? detail.field : '',
      message: typeof detail.message === 'string' ? detail.message : '',
    }))
    .filter((detail) => detail.field && detail.message);
}

function plsRouteLastStopIndex(routeStopCount?: number): number {
  return Math.max((routeStopCount ?? 2) - 1, 0);
}

/**
 * Rewrites pickup/consignee transport paths onto visible Route stop fields.
 * Destination uses consignee.* from BE so it never collides with stopoffs[0] → stops.1.
 */
function remapPlsRouteCreateLoadErrorPath(
  path: string,
  routeStopCount?: number,
  stops?: Array<{ stopType?: string } | undefined>
): string {
  if (!path) {
    return '';
  }

  const lastStopIndex = plsRouteLastStopIndex(routeStopCount);

  switch (path) {
    case 'pickup.readyTime':
      return 'stops.0.readyTime';
    case 'pickup.readyTimeEnd':
      return 'stops.0.readyEndTime';
    case 'consignee.mustDeliver':
      return `stops.${lastStopIndex}.mustDeliver`;
    case 'consignee.mustDeliverEnd':
      return `stops.${lastStopIndex}.readyEndTime`;
  }

  // BE maps stopoff earliest → readyTime; DROP intermediates show mustDeliver.
  const readyTimeMatch = path.match(/^stops\.(\d+)\.readyTime$/);
  if (readyTimeMatch) {
    const stopIndex = Number(readyTimeMatch[1]);
    if (stopIndex > 0 && isPlsDropoffStopType(stops?.[stopIndex]?.stopType)) {
      return `stops.${stopIndex}.mustDeliver`;
    }
  }

  return path;
}

/**
 * Maps PLS create-load API field names onto Route stop form paths.
 * Destination windows target the last stop (default index 1 for a 2-stop route).
 * stopoffs[i] are intermediates only → form stops[i + 1].
 */
function getPlsCreateLoadFormPath(
  field: unknown,
  routeStopCount?: number
): string {
  if (typeof field !== 'string') {
    return '';
  }

  const lastStopIndex = plsRouteLastStopIndex(routeStopCount);

  switch (field.trim()) {
    case 'customerId':
      return 'customer.externalTMSID';
    case 'customerLocationId':
      return 'plsSelectedLocationId';
    case 'customerBillToAddressId':
      return 'billTo.externalTMSID';
    case 'equipmentCode':
      return 'specifications.transportType';
    case 'loadSize':
      return 'mode';
    case 'referenceNumber':
      return 'customer.refNumber';
    case 'shipmentNumber':
      return 'plsOperationalFields.SHIPMENT_NUMBER';
    case 'loadServiceTypeIds':
      return 'plsLoadServiceTypeId';
    case 'origin.earliestDateTime':
      return 'stops.0.readyTime';
    case 'origin.latestDateTime':
      return 'stops.0.readyEndTime';
    case 'destination.earliestDateTime':
      return `stops.${lastStopIndex}.mustDeliver`;
    case 'destination.latestDateTime':
      return `stops.${lastStopIndex}.readyEndTime`;
  }

  const operationalField = field.trim().match(/^operationalFields\.(.+)$/);
  if (operationalField?.[1]) {
    return `plsOperationalFields.${operationalField[1]}`;
  }

  const commodityField = field.trim().match(/^commodities\[(\d+)\]\.(.+)$/);
  if (commodityField) {
    const [, index, property] = commodityField;
    switch (property) {
      case 'commodityName':
        return `commodities.${index}.description`;
      case 'weightLbs':
        return `commodities.${index}.weightTotal`;
      case 'totalPieces':
        return `commodities.${index}.quantity`;
      case 'unitType':
        // Form path is singular dimensionUnit (RHF), not dimensionsUnit JSON.
        return `commodities.${index}.dimensionUnit`;
      case 'length':
      case 'width':
      case 'height':
        return `commodities.${index}.${property}`;
      default:
        return '';
    }
  }

  const stopoffField = field.trim().match(/^stopoffs\[(\d+)\]\.(.+)$/);
  if (!stopoffField) {
    return '';
  }

  const stopoffIndex = Number(stopoffField[1]);
  if (!Number.isInteger(stopoffIndex) || stopoffIndex < 0) {
    return '';
  }

  const routeIndex = stopoffIndex + 1;
  const property = stopoffField[2];
  switch (property) {
    case 'earliestDateTime':
      // Pickup windows use readyTime; remap to mustDeliver for DROP via stops[].
      return `stops.${routeIndex}.readyTime`;
    case 'latestDateTime':
      return `stops.${routeIndex}.readyEndTime`;
    case 'companyName':
      return `stops.${routeIndex}.address.name`;
    case 'address.streetAddress1':
      return `stops.${routeIndex}.address.addressLine1`;
    case 'address.streetAddress2':
      return `stops.${routeIndex}.address.addressLine2`;
    case 'address.city':
      return `stops.${routeIndex}.address.city`;
    case 'address.stateCode':
      return `stops.${routeIndex}.address.state`;
    case 'address.zipcode':
      return `stops.${routeIndex}.address.zip`;
    case 'address.country':
      return `stops.${routeIndex}.address.country`;
    case 'contactName':
      return `stops.${routeIndex}.contact`;
    case 'contactNumber':
      return `stops.${routeIndex}.phone`;
    case 'appointmentNumber':
      return `stops.${routeIndex}.apptNum`;
    case 'pickupNumber':
    case 'deliveryNumber':
      return `stops.${routeIndex}.refNumber`;
    case 'stopAction':
      return `stops.${routeIndex}.stopType`;
    default:
      return '';
  }
}

export function getPlsRawLocationId(
  locationExternalTMSID: MaybeUndef<string>
): string {
  const selectedLocationId = (locationExternalTMSID ?? '').trim();
  if (!selectedLocationId) {
    return '';
  }

  const parts = selectedLocationId.split(':');
  if (parts.length === 3 && parts.every((part) => part.trim())) {
    return parts[2].trim();
  }

  return selectedLocationId;
}

export function createDefaultPlsCommodity(): Commodity {
  return {
    ...createNewCommodity({ dimensionUnit: 'in' }),
    overDimensional: false,
  } as Commodity & { overDimensional: boolean };
}

export function hasCompletePlsCommodity(
  commodities: Commodity[] | undefined
): boolean {
  return (normalizeToArray(commodities) ?? []).some(
    (commodity) =>
      !!commodity.description?.trim() &&
      Number(commodity.weightTotal) > 0 &&
      Number(commodity.quantity) > 0
  );
}

export function isValidPlsDateTimeWindow(
  earliest: unknown,
  latest: unknown
): boolean {
  if (!earliest || !latest) {
    return true;
  }

  const earliestTime = new Date(String(earliest)).getTime();
  const latestTime = new Date(String(latest)).getTime();
  if (Number.isNaN(earliestTime) || Number.isNaN(latestTime)) {
    return false;
  }

  return latestTime >= earliestTime;
}

export function initPlsLoadForm(): PlsFormValues {
  const baseLoad = normalizeLoad(TMS.PLS, {
    ...createInitLoad(),
    mode: PlsLoadSize.FTL,
    specifications: {
      ...createInitSpecs(),
      transportType: '',
      transportSize: PlsLoadSize.FTL,
      serviceType: '',
    },
    commodities: [createDefaultPlsCommodity()],
  });
  const withAppointmentDefaults = {
    ...baseLoad,
    pickup: {
      ...baseLoad.pickup,
      apptType: PLS_APPOINTMENT_TYPE.FCFS,
    },
    consignee: {
      ...baseLoad.consignee,
      apptType: PLS_APPOINTMENT_TYPE.FCFS,
    },
  };
  const stops = ensurePlsRouteStops(withAppointmentDefaults);
  const synced = syncPlsRouteStopsToPickupConsignee(stops);

  return {
    ...withAppointmentDefaults,
    ...synced,
    stops,
    moreThanTwoStops: false,
    plsSelectedLocationId: '',
    plsOperationalFields: {},
    plsLoadReferenceFields: {},
    plsPickupReferenceFields: {},
    plsConsigneeReferenceFields: {},
    plsLoadServiceTypeId: '',
    plsPendingReferences: [],
  };
}

/** Rebuild Route stops for drafts saved before the Route UI (pickup/consignee only). */
export function hydratePlsFormRouteStops(values: PlsFormValues): PlsFormValues {
  const stops = ensurePlsRouteStops({
    stops: normalizeToArray(values.stops) ?? [],
    pickup: values.pickup,
    consignee: values.consignee,
  });
  const synced = syncPlsRouteStopsToPickupConsignee(stops);

  return {
    ...values,
    ...synced,
    stops,
    moreThanTwoStops: isPlsMultiStopRoute(stops),
    // ENG-8509: bare Reference # was removed from the create form; clear any
    // draft-persisted value so it cannot still submit as PLS referenceNumber.
    ...(values.customer
      ? {
          customer: {
            ...values.customer,
            refNumber: '',
          },
        }
      : {}),
  };
}

export function buildPlsOperationalReferences(
  operationalFields: Record<string, string>
): AdditionalReference[] {
  return Object.entries(operationalFields)
    .filter(([, value]) => (value ?? '').trim().length > 0)
    .map(([code, value]) => ({
      qualifier: code,
      number: (value ?? '').trim(),
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    }));
}

export type PlsReferenceFieldMapName =
  | 'plsLoadReferenceFields'
  | 'plsPickupReferenceFields'
  | 'plsConsigneeReferenceFields';

function omitAdditionalReferenceQualifier(
  references: AdditionalReference[] | undefined,
  code: string
): AdditionalReference[] {
  return (normalizeToArray(references) ?? []).filter(
    (reference) => reference.qualifier !== code
  );
}

/**
 * Removes a catalog reference from the form map and the hydrated
 * additionalReferences source so submit merge cannot resurrect it.
 */
export function omitPlsReferenceField(
  values: PlsFormValues,
  scope: PlsReferenceFieldMapName,
  code: string
): Partial<PlsFormValues> {
  const nextMap = { ...(values[scope] ?? {}) };
  delete nextMap[code];

  if (scope === 'plsLoadReferenceFields') {
    return {
      plsLoadReferenceFields: nextMap,
      additionalReferences: omitAdditionalReferenceQualifier(
        values.additionalReferences,
        code
      ),
    };
  }

  if (scope === 'plsPickupReferenceFields') {
    return {
      plsPickupReferenceFields: nextMap,
      pickup: {
        ...values.pickup,
        additionalReferences: omitAdditionalReferenceQualifier(
          values.pickup?.additionalReferences,
          code
        ),
      },
    };
  }

  return {
    plsConsigneeReferenceFields: nextMap,
    consignee: {
      ...values.consignee,
      additionalReferences: omitAdditionalReferenceQualifier(
        values.consignee?.additionalReferences,
        code
      ),
    },
  };
}

// Bidirectional partition against the bill-to operational catalog.
// - Load refs whose code is in the catalog → plsOperationalFields (canonical casing)
// - Ops fields whose code is not in the catalog → plsLoadReferenceFields
// Empty catalog moves every ops field back to load refs so a stale partition
// from a previous bill-to cannot hide values after suggestion/bill-to switch.
export function partitionPlsReferenceFieldsByOperationalCodes(
  values: PlsReferenceFieldValues,
  operationalFieldCodes: readonly string[]
): PlsPartitionedReferenceFields {
  const operationalByUpper = new Map(
    operationalFieldCodes
      .map((code) => code.trim())
      .filter(Boolean)
      .map((code) => [code.toUpperCase(), code] as const)
  );
  const plsOperationalFields = { ...(values.plsOperationalFields ?? {}) };
  const plsLoadReferenceFields = { ...(values.plsLoadReferenceFields ?? {}) };
  let changed = false;

  for (const [code, value] of Object.entries(plsOperationalFields)) {
    const canonical = operationalByUpper.get(code.trim().toUpperCase());
    if (canonical) {
      if (code !== canonical) {
        plsOperationalFields[canonical] = value;
        delete plsOperationalFields[code];
        changed = true;
      }
      continue;
    }
    if (!(code in plsLoadReferenceFields)) {
      plsLoadReferenceFields[code] = value;
    }
    delete plsOperationalFields[code];
    changed = true;
  }

  for (const [code, value] of Object.entries(plsLoadReferenceFields)) {
    const canonical = operationalByUpper.get(code.trim().toUpperCase());
    if (!canonical) {
      continue;
    }
    plsOperationalFields[canonical] = value;
    delete plsLoadReferenceFields[code];
    changed = true;
  }

  return {
    plsOperationalFields,
    plsLoadReferenceFields,
    changed,
  };
}

export function applyPlsOperationalFieldPartition(
  values: PlsFormValues,
  operationalFieldCodes: readonly string[]
): PlsFormValues {
  const partitioned = partitionPlsReferenceFieldsByOperationalCodes(
    values,
    operationalFieldCodes
  );
  const movedLoadRefQualifiers = Object.keys(
    values.plsLoadReferenceFields ?? {}
  ).filter((code) => !(code in partitioned.plsLoadReferenceFields));

  return {
    ...values,
    plsOperationalFields: partitioned.plsOperationalFields,
    plsLoadReferenceFields: partitioned.plsLoadReferenceFields,
    ...(movedLoadRefQualifiers.length > 0
      ? {
          additionalReferences: (
            normalizeToArray(values.additionalReferences) ?? []
          ).filter(
            (reference) => !movedLoadRefQualifiers.includes(reference.qualifier)
          ),
        }
      : {}),
  };
}

export function buildPlsCreateLoadPayload(
  data: PlsFormValues,
  tmsID: number
): Load {
  const {
    plsOperationalFields: rawOperationalFields,
    plsLoadReferenceFields: rawLoadReferenceFields,
    plsPickupReferenceFields: rawPickupReferenceFields,
    plsConsigneeReferenceFields: rawConsigneeReferenceFields,
    plsLoadServiceTypeId,
    plsSelectedLocationId,
    // Cards without a type; Build Load stops on any with a number first.
    plsPendingReferences: _plsPendingReferences,
    ...loadFields
  } = data;
  const plsOperationalFields = rawOperationalFields ?? {};
  const plsLoadReferenceFields = rawLoadReferenceFields ?? {};
  const plsPickupReferenceFields = rawPickupReferenceFields ?? {};
  const plsConsigneeReferenceFields = rawConsigneeReferenceFields ?? {};
  const loadFieldReferences = buildPlsOperationalReferences({
    ...plsLoadReferenceFields,
    ...plsOperationalFields,
  });
  const pickupReferenceFields = buildPlsOperationalReferences(
    plsPickupReferenceFields
  );
  const consigneeReferenceFields = buildPlsOperationalReferences(
    plsConsigneeReferenceFields
  );
  const selectedLocationId = getPlsRawLocationId(plsSelectedLocationId);
  const customerLocationReferences: AdditionalReference[] = selectedLocationId
    ? [
        {
          qualifier: PLS_CUSTOMER_LOCATION_QUALIFIER,
          number: selectedLocationId,
          weight: 0,
          pieces: 0,
          shouldSendToDriver: false,
        },
      ]
    : [];
  const loadServiceTypeId = (plsLoadServiceTypeId ?? '').trim();
  const loadServiceTypeReferences: AdditionalReference[] = loadServiceTypeId
    ? [
        {
          qualifier: PLS_LOAD_SERVICE_TYPE_ID_QUALIFIER,
          number: loadServiceTypeId,
          weight: 0,
          pieces: 0,
          shouldSendToDriver: false,
        },
      ]
    : [];

  // PLS form value -> Drumkit mode: PLS's "FTL" is Drumkit's TL.
  const loadSize =
    loadFields.mode === PlsLoadSize.LTL ? PlsLoadSize.LTL : PlsLoadSize.FTL;

  const routeStops = normalizePlsRouteStopTypes(
    ensurePlsRouteStops({
      stops: normalizeToArray(loadFields.stops) ?? [],
      pickup: loadFields.pickup,
      consignee: loadFields.consignee,
    })
  );
  const syncedEnds = syncPlsRouteStopsToPickupConsignee(routeStops);
  const isMultiStop = isPlsMultiStopRoute(routeStops);
  const formattedStops = routeStops.map((stop) =>
    formatPlsRouteStopDatesForSubmit(stop)
  );

  return {
    ...loadFields,
    tmsID,
    mode: loadSize === PlsLoadSize.LTL ? 'LTL' : 'TL',
    moreThanTwoStops: isMultiStop,
    stops: formattedStops,
    specifications: {
      ...loadFields.specifications,
      transportSize: loadSize,
    },
    accessorials: (normalizeToArray(loadFields.accessorials) ?? [])
      .map((accessorial) => {
        const code =
          accessorial.code || plsAccessorialCodeFromValue(accessorial.value);
        return {
          id: accessorial.id != null ? Number(accessorial.id) : 0,
          displayText: accessorial.displayText || code || '',
          code,
          value: accessorial.value ?? code,
        } satisfies PlsFormAccessorialItem;
      })
      .filter((accessorial) =>
        Boolean(accessorial.code || accessorial.displayText || accessorial.id)
      ),
    // RHF / FormStorage can turn arrays into numeric-key objects; Go expects []LineItems.
    rateData: {
      ...loadFields.rateData,
      customerLineItems:
        normalizeToArray(loadFields.rateData?.customerLineItems) ?? [],
      carrierLineItems:
        normalizeToArray(loadFields.rateData?.carrierLineItems) ?? [],
    },
    commodities: (normalizeToArray(loadFields.commodities) ?? []).map(
      (commodity) => ({
        ...commodity,
        dimensionsUnit:
          commodity.dimensionUnit ||
          ((commodity as Commodity & { dimensionsUnit?: string })
            .dimensionsUnit ??
            'in'),
      })
    ) as Load['commodities'],
    pickup: formatPlsStopDatesForSubmit(
      {
        ...syncedEnds.pickup,
        additionalReferences: mergePlsReferences(
          normalizeToArray(syncedEnds.pickup.additionalReferences),
          pickupReferenceFields
        ),
      } as Pickup,
      'pickup'
    ) as Pickup,
    consignee: formatPlsStopDatesForSubmit(
      {
        ...syncedEnds.consignee,
        additionalReferences: mergePlsReferences(
          normalizeToArray(syncedEnds.consignee.additionalReferences),
          consigneeReferenceFields
        ),
      } as Dropoff,
      'dropoff'
    ) as Dropoff,
    additionalReferences: [
      ...(normalizeToArray(loadFields.additionalReferences) ?? []).filter(
        (reference) =>
          reference.qualifier !== PLS_CUSTOMER_LOCATION_QUALIFIER &&
          reference.qualifier !== PLS_LOAD_SERVICE_TYPE_ID_QUALIFIER &&
          !(reference.qualifier in plsLoadReferenceFields) &&
          !(reference.qualifier in plsOperationalFields)
      ),
      ...customerLocationReferences,
      ...loadServiceTypeReferences,
      ...loadFieldReferences,
    ],
  } as Load;
}

function mergePlsReferences(
  existing: AdditionalReference[] | undefined,
  replacements: AdditionalReference[]
): AdditionalReference[] {
  const replacementQualifiers = new Set(
    replacements.map((reference) => reference.qualifier)
  );

  return [
    ...(existing ?? []).filter(
      (reference) => !replacementQualifiers.has(reference.qualifier)
    ),
    ...replacements,
  ];
}

export function createEmptyPlsBillTo(): CompanyCoreInfo {
  return createInitCompanyCoreInfo();
}

const PLS_BUILT_LOAD_DATETIME_FIELDS = [
  'readyTime',
  'readyTimeEnd',
  'readyEndTime',
  'mustDeliver',
  'mustDeliverEnd',
  'apptStartTime',
  'apptEndTime',
] as const;

/**
 * normalizeLoad shifts timezone-agnostic TMS dates through the browser offset.
 * PLS submit reads UTC components as the wall clock and does not reverse that
 * shift, so built-load hydration must restore the original clock digits.
 */
function applyPlsWallClockStopTimes<T extends ObjectData>(
  stop: T | undefined,
  source: ObjectData | undefined
): T | undefined {
  if (!stop || !source) {
    return stop;
  }

  const next: ObjectData = { ...stop };
  for (const field of PLS_BUILT_LOAD_DATETIME_FIELDS) {
    if (!(field in source)) {
      continue;
    }

    const raw = source[field];
    if (raw == null || raw === '') {
      next[field] = raw ?? '';
      continue;
    }

    const parsed = parsePlsSuggestionWallClock(raw);
    if (parsed) {
      next[field] = parsed;
    }
  }

  return next as T;
}

export function mapBuiltLoadToPlsFormValues(
  load: Load,
  operationalFieldCodes: readonly string[] = []
): PlsFormValues {
  const normalizedLoad = normalizeLoad(TMS.PLS, load) as PlsFormValues;
  const selectedLocationReference = normalizedLoad.additionalReferences?.find(
    (reference) => reference.qualifier === PLS_CUSTOMER_LOCATION_QUALIFIER
  );
  const rawStops = normalizeToArray(load.stops) ?? [];
  const pickup = normalizePlsStopAppointmentFields(
    applyPlsWallClockStopTimes(normalizedLoad.pickup, load.pickup),
    'pickup'
  ) as PlsFormValues['pickup'];
  const consignee = normalizePlsStopAppointmentFields(
    applyPlsWallClockStopTimes(normalizedLoad.consignee, load.consignee),
    'dropoff'
  ) as PlsFormValues['consignee'];
  const stops = ensurePlsRouteStops({
    stops: (normalizeToArray(normalizedLoad.stops) ?? []).map((stop, index) =>
      applyPlsWallClockStopTimes(stop, rawStops[index] as ObjectData)
    ) as LoadStopsItem[],
    pickup,
    consignee,
  });

  const rawAccessorials = (
    load as unknown as { accessorials?: PlsFormAccessorialItem[] }
  ).accessorials;
  const accessorials: PlsFormAccessorialItem[] | undefined = rawAccessorials
    ? (normalizeToArray(rawAccessorials) ?? []).map((acc, index) => {
        const fallback = normalizedLoad.accessorials?.[index];
        const code = acc.code || plsAccessorialCodeFromValue(acc.value);
        return {
          id: acc.id != null ? Number(acc.id) : (fallback?.id ?? 0),
          displayText: acc.displayText || fallback?.displayText || code || '',
          code,
          value: acc.value ?? fallback?.value ?? code,
        };
      })
    : normalizedLoad.accessorials;

  return applyPlsOperationalFieldPartition(
    {
      ...normalizedLoad,
      accessorials,
      // Drumkit mode -> PLS form value: Drumkit's TL is PLS's "FTL".
      mode: load.mode === 'TL' ? PlsLoadSize.FTL : load.mode,
      pickup,
      consignee,
      stops,
      moreThanTwoStops: isPlsMultiStopRoute(stops),
      commodities: normalizedLoad.commodities.map((commodity) => ({
        ...commodity,
        packagingType:
          commodity.packagingType || commodity.handlingUnitTypeName || '',
        weightTotal: commodity.weightTotal || commodity.grossWeight?.val || 0,
        quantity: commodity.quantity || commodity.totalPieces?.val || 0,
        dimensionUnit:
          commodity.dimensionUnit ||
          ((commodity as Commodity & { dimensionsUnit?: string })
            .dimensionsUnit ??
            'in'),
      })),
      plsSelectedLocationId: selectedLocationReference?.number ?? '',
      plsOperationalFields: {},
      plsLoadReferenceFields: referencesToPlsFieldValues(
        normalizedLoad.additionalReferences,
        [...PLS_INTERNAL_REFERENCE_QUALIFIERS]
      ),
      plsPickupReferenceFields: referencesToPlsFieldValues(
        normalizedLoad.pickup.additionalReferences
      ),
      plsConsigneeReferenceFields: referencesToPlsFieldValues(
        normalizedLoad.consignee.additionalReferences
      ),
      plsLoadServiceTypeId:
        normalizedLoad.additionalReferences?.find(
          (reference) =>
            reference.qualifier === PLS_LOAD_SERVICE_TYPE_ID_QUALIFIER
        )?.number ?? '',
    },
    operationalFieldCodes
  );
}

export function mapPlsSuggestionToFormValues(
  suggested: Partial<NormalizedLoad>
): Partial<PlsFormValues> {
  // Drumkit mode -> PLS form value: Drumkit's TL is PLS's "FTL".
  let mode = suggested.mode;
  if (suggested.mode === 'TL' || suggested.mode === PlsLoadSize.FTL) {
    mode = PlsLoadSize.FTL;
  } else if (suggested.mode === 'LTL' || suggested.mode === PlsLoadSize.LTL) {
    mode = PlsLoadSize.LTL;
  }

  const commodities = suggested.commodities?.map((commodity) => ({
    ...commodity,
    dimensionUnit:
      commodity.dimensionUnit ||
      ((commodity as Commodity & { dimensionsUnit?: string }).dimensionsUnit ??
        'in'),
  }));

  // Kit often sends generic labels like "Any"; PLS expects catalog IDs via
  // plsLoadServiceTypeId. Keep only numeric IDs that can be sent to PLS.
  const suggestedServiceType = (
    suggested.specifications?.serviceType ?? ''
  ).trim();
  const numericServiceTypeId =
    /^\d+$/.test(suggestedServiceType) && Number(suggestedServiceType) > 0
      ? suggestedServiceType
      : '';

  const pickup = suggested.pickup
    ? (normalizePlsStopAppointmentFields(
        suggested.pickup as ObjectData,
        'pickup',
        true
      ) as PlsFormValues['pickup'])
    : undefined;
  const consignee = suggested.consignee
    ? (normalizePlsStopAppointmentFields(
        suggested.consignee as ObjectData,
        'dropoff',
        true
      ) as PlsFormValues['consignee'])
    : undefined;
  const stops = ensurePlsRouteStops({
    stops: (normalizeToArray(suggested.stops) ?? []).map((stop) =>
      preferPlsRouteStopAppointmentWindow(stop as LoadStopsItem)
    ),
    pickup,
    consignee,
  });

  // Route AI refs into typed maps as-is (ENG-8415: no FE qualifier alias remap).
  // Exact catalog matches move into Customer Reference Fields once ops catalog loads.
  let plsLoadReferenceFields = referencesToPlsFieldValues(
    suggested.additionalReferences,
    [...PLS_INTERNAL_REFERENCE_QUALIFIERS]
  );
  const poNums = (suggested.poNums ?? '').trim();
  if (poNums) {
    const hasPurchaseOrder = Object.keys(plsLoadReferenceFields).some(
      (code) => code.trim().toUpperCase() === PLS_PURCHASE_ORDER_FIELD_CODE
    );
    if (!hasPurchaseOrder) {
      plsLoadReferenceFields = {
        ...plsLoadReferenceFields,
        [PLS_PURCHASE_ORDER_FIELD_CODE]: poNums,
      };
    }
  }

  // Drop load-level refs that now live in typed maps so create/update cannot
  // double-send them, and so omitPlsReferenceField stays authoritative.
  const routedLoadQualifiers = new Set(Object.keys(plsLoadReferenceFields));
  const additionalReferences = (
    normalizeToArray(suggested.additionalReferences) ?? []
  ).filter((reference) => !routedLoadQualifiers.has(reference.qualifier));

  return {
    ...suggested,
    // Typed PURCHASE_ORDER owns PO; clearing poNums prevents BE from
    // re-adding it after the user deletes the visible field.
    poNums: '',
    ...(mode ? { mode } : {}),
    ...(commodities ? { commodities } : {}),
    ...(suggested.rateData
      ? {
          rateData: {
            ...suggested.rateData,
            customerLineItems:
              normalizeToArray(suggested.rateData.customerLineItems) ?? [],
            carrierLineItems:
              normalizeToArray(suggested.rateData.carrierLineItems) ?? [],
          },
        }
      : {}),
    ...(suggested.specifications
      ? {
          specifications: {
            ...suggested.specifications,
            transportType: suggestedTransportTypeOrEmpty(
              suggested.specifications
            ),
            serviceType: numericServiceTypeId,
          },
        }
      : {}),
    ...(pickup ? { pickup } : {}),
    ...(consignee ? { consignee } : {}),
    ...(suggested.customer
      ? {
          // ENG-8509: bare Reference # was removed; do not keep a hidden
          // customer.refNumber → PLS referenceNumber on create.
          customer: {
            ...suggested.customer,
            refNumber: '',
          },
        }
      : {}),
    stops,
    moreThanTwoStops: isPlsMultiStopRoute(stops),
    billTo: suggested.billTo ?? createEmptyPlsBillTo(),
    additionalReferences,
    plsSelectedLocationId: '',
    plsOperationalFields: {},
    plsLoadReferenceFields,
    plsPickupReferenceFields: referencesToPlsFieldValues(
      suggested.pickup?.additionalReferences
    ),
    plsConsigneeReferenceFields: referencesToPlsFieldValues(
      suggested.consignee?.additionalReferences
    ),
    plsLoadServiceTypeId: numericServiceTypeId,
  };
}

function referencesToPlsFieldValues(
  references: AdditionalReference[] | undefined | unknown,
  excludedQualifiers: string[] = []
): Record<string, string> {
  const excluded = new Set(excludedQualifiers);

  return Object.fromEntries(
    (normalizeToArray(references as AdditionalReference[] | undefined) ?? [])
      .filter(
        (reference) =>
          !excluded.has(reference.qualifier) &&
          !!reference.qualifier?.trim() &&
          !!reference.number?.trim()
      )
      .map((reference) => [reference.qualifier, reference.number])
  );
}

export function getPlsLocationBillToAddressId(
  location: TMSLocation | undefined
): string {
  if (!location?.notes) {
    return '';
  }

  const match = location.notes.match(
    new RegExp(`(?:^|;)\\s*${PLS_BILL_TO_ADDRESS_NOTE_PREFIX}(\\d+)\\s*(?:;|$)`)
  );

  return match?.[1] ?? '';
}

export function getPlsLocationBillToAddressName(
  location: TMSLocation | undefined
): string {
  if (!location?.notes) {
    return '';
  }

  const match = location.notes.match(
    new RegExp(
      `(?:^|;)\\s*${PLS_BILL_TO_ADDRESS_NAME_NOTE_PREFIX}([^;]*)(?:;|$)`
    )
  );

  return (match?.[1] ?? '').trim();
}

export function buildPlsBillTosFromLocations(
  locations: TMSLocation[] | undefined | null
): CompanyCoreInfo[] {
  const billToNamesById = new Map<string, string>();

  for (const location of locations ?? []) {
    const billToId = getPlsLocationBillToAddressId(location);
    if (!billToId) {
      continue;
    }

    const billToName = getPlsLocationBillToAddressName(location);
    const existingName = billToNamesById.get(billToId);
    if (existingName === undefined) {
      billToNamesById.set(billToId, billToName);
      continue;
    }

    if (!existingName && billToName) {
      billToNamesById.set(billToId, billToName);
    }
  }

  return Array.from(billToNamesById.entries()).map(
    ([externalTMSID, billToName]) => ({
      ...createEmptyPlsBillTo(),
      externalTMSID,
      name: billToName || `Bill To ${externalTMSID}`,
    })
  );
}

export function getAutomaticPlsBillTo(
  locations: Maybe<TMSLocation[]>,
  selectedLocationId: string
): Undef<CompanyCoreInfo> {
  const availableBillTos = buildPlsBillTosFromLocations(locations);
  const selectedRawLocationId = getPlsRawLocationId(selectedLocationId);
  const selectedLocation = selectedRawLocationId
    ? (locations ?? []).find(
        (location) =>
          getPlsRawLocationId(location.externalTMSID) === selectedRawLocationId
      )
    : undefined;
  const selectedLocationBillToId =
    getPlsLocationBillToAddressId(selectedLocation);

  if (selectedLocationBillToId) {
    return availableBillTos.find(
      (billTo) => billTo.externalTMSID === selectedLocationBillToId
    );
  }

  return availableBillTos.length === 1 ? availableBillTos[0] : undefined;
}

export enum PlsBillToTransitionAction {
  Preserve = 'preserve',
  Replace = 'replace',
  Clear = 'clear',
}

export type PlsBillToTransition = {
  action: PlsBillToTransitionAction;
  nextAutoBillToId?: string;
};

// Explicit suggestion/manual values own the field. Only an empty or previously
// auto-owned value may change when the canonical location catalog changes.
export function getPlsBillToTransition(
  currentBillToId: string | undefined,
  previousAutoBillToId: Undef<string>,
  automaticBillToId: Undef<string>,
  explicitSuggestedBillToId: Undef<string>
): PlsBillToTransition {
  const currentId = (currentBillToId ?? '').trim();
  const previousAutoId = previousAutoBillToId?.trim();
  const automaticId = automaticBillToId?.trim();
  const suggestedId = explicitSuggestedBillToId?.trim();

  if (suggestedId && currentId === suggestedId) {
    return { action: PlsBillToTransitionAction.Preserve };
  }

  if (currentId && currentId !== previousAutoId) {
    return { action: PlsBillToTransitionAction.Preserve };
  }

  if (automaticId) {
    if (currentId === automaticId && previousAutoId === automaticId) {
      return { action: PlsBillToTransitionAction.Preserve };
    }
    return {
      action: PlsBillToTransitionAction.Replace,
      nextAutoBillToId: automaticId,
    };
  }

  if (currentId && currentId === previousAutoId) {
    return { action: PlsBillToTransitionAction.Clear };
  }

  return { action: PlsBillToTransitionAction.Preserve };
}

export function mapPlsBillTosToSelectOptions(
  billTos: Maybe<CompanyCoreInfo[]>
): ValueType[] {
  if (!billTos) {
    return [];
  }

  return billTos.map((billTo) => ({
    value: billTo.externalTMSID,
    name: billTo.name,
    addressLine1: billTo.addressLine1,
    addressLine2: billTo.addressLine2,
    city: billTo.city,
    state: billTo.state,
    label: `${billTo.name}`,
  }));
}

// All of the customer's locations, those under the bill-to first, in their original order.
export function plsLocationsBillToFirst(
  locations: MaybeUndef<Maybe<TMSLocation[]>>,
  billToId: MaybeUndef<string>
): TMSLocation[] {
  const all = locations ?? [];
  const id = billToId?.trim();
  if (!id) {
    return all;
  }
  const isUnderBillTo = (location: TMSLocation) =>
    getPlsLocationBillToAddressId(location) === id;
  return [
    ...all.filter(isUnderBillTo),
    ...all.filter((location) => !isUnderBillTo(location)),
  ];
}

// The customer's locations under a bill-to. When none sit under it but the locations say which
// bill-to they sit under, they all sit under others, so none is a fit (picking one would pair the
// bill-to with another's location). All of them only when the locations name no bill-to at all.
export function plsLocationsForBillTo(
  locations: MaybeUndef<Maybe<TMSLocation[]>>,
  billToId: MaybeUndef<string>
): TMSLocation[] {
  const all = locations ?? [];
  const id = billToId?.trim();
  if (!id) {
    return all;
  }
  const underBillTo = all.filter(
    (location) => getPlsLocationBillToAddressId(location) === id
  );
  if (underBillTo.length > 0) {
    return underBillTo;
  }
  return all.some((location) => getPlsLocationBillToAddressId(location))
    ? []
    : all;
}

export function getAutomaticPlsLocationId(
  locations: MaybeUndef<TMSLocation[]>
): Undef<string> {
  if (!locations || locations.length !== 1) {
    return undefined;
  }
  const rawId = getPlsRawLocationId(locations[0]?.externalTMSID);
  return rawId || undefined;
}

export enum PlsLocationTransitionAction {
  Preserve = 'preserve',
  Replace = 'replace',
  Clear = 'clear',
}

export type PlsLocationTransition = {
  action: PlsLocationTransitionAction;
  nextAutoLocationId?: string;
};

export function getPlsLocationTransition(
  currentLocationId: Undef<string>,
  previousAutoLocationId: Undef<string>,
  automaticLocationId: Undef<string>
): PlsLocationTransition {
  const currentId = (currentLocationId ?? '').trim();
  const previousAutoId = previousAutoLocationId?.trim();
  const automaticId = automaticLocationId?.trim();

  if (currentId && currentId !== previousAutoId) {
    return { action: PlsLocationTransitionAction.Preserve };
  }

  if (automaticId) {
    if (currentId === automaticId && previousAutoId === automaticId) {
      return { action: PlsLocationTransitionAction.Preserve };
    }
    return {
      action: PlsLocationTransitionAction.Replace,
      nextAutoLocationId: automaticId,
    };
  }

  if (currentId && currentId === previousAutoId) {
    return { action: PlsLocationTransitionAction.Clear };
  }

  return { action: PlsLocationTransitionAction.Preserve };
}

// Select options for PLS's accessorial catalog: one per code, labeled "Name (CODE)".
export function plsAccessorialOptions(
  catalog: PlsAccessorial[]
): { value: string; label: string }[] {
  return catalog.reduce<{ value: string; label: string }[]>(
    (acc, accessorial) => {
      const code = String(accessorial?.code ?? '')
        .trim()
        .toUpperCase();
      if (!code || acc.some((option) => option.value === code)) {
        return acc;
      }

      const label =
        accessorial.name && accessorial.name.trim() !== code
          ? `${accessorial.name} (${code})`
          : accessorial.name || code;

      acc.push({ value: code, label });
      return acc;
    },
    []
  );
}
